import { useState } from 'react';
import { doc, updateDoc, writeBatch } from 'firebase/firestore';
import { db } from '../../../firebaseConfig';
import { isKingRole, getPieceSymbols, getVoteWeight, hasActiveVoters, groupSuggestionsByMove } from '../utils/gameUtils';
import { buildMoveUpdateData, buildGameOverUpdate } from '../utils/moveBuilder';
import { batchPushChat } from '../utils/chatStore';

// Manages voting: poll creation, voting, move execution (winning, random, any legal)
export const useVoting = (gameId, game, chess, currentUser, isAdmin, playerData, playerTeam, setChessPosition, startTimer) => {
  const [selectedMoves, setSelectedMoves] = useState([]);

  const hasKingRoleCheck = () => isKingRole(playerData?.role);

  // Execute any random legal move (when team fails to suggest anything)
  const executeAnyLegalMove = async (reason) => {
    if (!playerTeam) return;

    try {
      const legalMoves = chess.moves({ verbose: true });
      if (legalMoves.length === 0) {
        console.log('No legal moves available');
        return;
      }

      const randomIndex = Math.floor(Math.random() * legalMoves.length);
      const randomLegalMove = legalMoves[randomIndex];
      const pieceSymbols = getPieceSymbols(playerTeam);

      const moveData = {
        from: randomLegalMove.from,
        to: randomLegalMove.to,
        notation: randomLegalMove.san,
        displayText: `${pieceSymbols[randomLegalMove.piece] || ''} ${randomLegalMove.san}`
      };

      await executeRandomMove(moveData, reason);
    } catch (error) {
      console.error('Error executing random legal move:', error);
    }
  };

  // Execute a random move directly (fallback when team fails to decide)
  const executeRandomMove = async (moveData, reason) => {
    if (!playerTeam || !moveData) return;

    try {
      const move = chess.move({
        from: moveData.from,
        to: moveData.to,
        promotion: 'q'
      });

      if (!move) {
        console.log('Random move is no longer valid');
        return;
      }

      const gameRef = doc(db, 'games', gameId);
      const newFen = chess.fen();
      const newTurn = chess.turn() === 'w' ? 'white' : 'black';

      const chatMessage = {
        sender: 'SYSTEM',
        message: `${reason}: ${moveData.displayText || moveData.notation}`,
        timestamp: new Date().toISOString(),
        isMove: true,
        isRandom: true
      };

      const { updateData, chats } = buildMoveUpdateData({
        game, playerTeam, move,
        sourceSquare: moveData.from,
        targetSquare: moveData.to,
        newFen, newTurn, chatMessage,
        wasRandom: true,
        hasKingRole: hasKingRoleCheck(),
      });

      const batch = writeBatch(db);
      batch.update(gameRef, updateData);
      chats.forEach(c => batchPushChat(batch, gameId, c));
      await batch.commit();
      await startTimer(newTurn, 'suggestion', 90);

      // Check for game end
      const gameOverUpdate = buildGameOverUpdate(chess, playerTeam);
      if (gameOverUpdate) {
        await updateDoc(gameRef, gameOverUpdate);
      }
    } catch (error) {
      console.error('Error executing random move:', error);
    }
  };

  // Create a poll from selected moves (King only)
  const createPoll = async (selectedMoveIds) => {
    if (game?.paused) return;
    if (!hasKingRoleCheck()) return;
    if (selectedMoveIds.length === 0 || selectedMoveIds.length > 3) return;

    // If no active voters, King executes directly
    if (!hasActiveVoters(game, playerTeam)) {
      await executeWinningMove(selectedMoveIds[0], false);
      return;
    }

    try {
      const gameRef = doc(db, 'games', gameId);
      const pollField = playerTeam === 'white' ? 'activePolls.white' : 'activePolls.black';

      const poll = {
        moves: selectedMoveIds,
        votes: {},
        createdAt: new Date().toISOString(),
        createdBy: currentUser.email,
        createdByName: playerData?.nickname || currentUser.email
      };

      await updateDoc(gameRef, { [pollField]: poll });
      await startTimer(playerTeam, 'voting', 30);
    } catch (error) {
      console.error('Error creating poll:', error);
    }
  };

  // Vote on a move in the poll
  const voteOnMove = async (moveId) => {
    if (!playerTeam) return;
    if (game?.paused) return;
    if (hasKingRoleCheck()) return;
    if (game.eliminatedPlayers?.includes(currentUser.email)) return;

    try {
      const gameRef = doc(db, 'games', gameId);
      const activePoll = playerTeam === 'white' ? game.activePolls?.white : game.activePolls?.black;
      if (!activePoll) return;

      const updatedVotes = { ...activePoll.votes, [currentUser.email]: moveId };
      const pollField = playerTeam === 'white' ? 'activePolls.white.votes' : 'activePolls.black.votes';

      await updateDoc(gameRef, { [pollField]: updatedVotes });
    } catch (error) {
      console.error('Error voting:', error);
    }
  };

  // Execute the winning move
  const executeWinningMove = async (manualMoveId = null, forceRandom = false) => {
    if (!playerTeam) return;
    if (game?.paused) return;

    const activePoll = playerTeam === 'white' ? game.activePolls?.white : game.activePolls?.black;
    const suggestions = playerTeam === 'white' ? game.suggestedMoves?.white : game.suggestedMoves?.black;
    const teamPlayers = playerTeam === 'white' ? game.whiteTeam : game.blackTeam;

    if (!activePoll || !suggestions) return;

    // Calculate weighted votes
    const voteCounts = {};
    Object.entries(activePoll.votes || {}).forEach(([voterEmail, moveId]) => {
      const voter = teamPlayers?.find(p => p.email === voterEmail);
      if (voter) {
        const weight = getVoteWeight(voter.role);
        voteCounts[moveId] = (voteCounts[moveId] || 0) + weight;
      }
    });

    let winningMoveId = manualMoveId;
    let maxVotes = manualMoveId ? voteCounts[manualMoveId] || 0 : 0;
    let wasRandomPick = false;

    if (!manualMoveId) {
      const sortedMoves = Object.entries(voteCounts).sort((a, b) => b[1] - a[1]);

      if (sortedMoves.length === 0) {
        if (forceRandom && activePoll.moves && activePoll.moves.length > 0) {
          const randomIndex = Math.floor(Math.random() * activePoll.moves.length);
          winningMoveId = activePoll.moves[randomIndex];
          maxVotes = 0;
          wasRandomPick = true;
        } else if (forceRandom) {
          await executeAnyLegalMove('No moves in poll - random legal move executed');
          return;
        } else {
          return;
        }
      } else {
        const topVotes = sortedMoves[0][1];
        const tiedMoves = sortedMoves.filter(([_, votes]) => votes === topVotes);

        if (tiedMoves.length > 1) {
          if (forceRandom) {
            const randomIndex = Math.floor(Math.random() * tiedMoves.length);
            winningMoveId = tiedMoves[randomIndex][0];
            maxVotes = tiedMoves[randomIndex][1];
            wasRandomPick = true;
          } else {
            return; // Tie - King must select
          }
        } else {
          winningMoveId = sortedMoves[0][0];
          maxVotes = sortedMoves[0][1];
        }
      }
    }

    if (!winningMoveId) return;

    const winningMove = suggestions.find(s => s.id === winningMoveId);
    if (!winningMove) return;

    try {
      const move = chess.move({
        from: winningMove.from,
        to: winningMove.to,
        promotion: 'q'
      });

      if (!move) return;

      const gameRef = doc(db, 'games', gameId);
      const newFen = chess.fen();
      const newTurn = chess.turn() === 'w' ? 'white' : 'black';

      const chatMessage = {
        sender: 'SYSTEM',
        message: wasRandomPick
          ? `Random move executed (tie/no votes): ${winningMove.displayText}`
          : `Move executed: ${winningMove.displayText} (${maxVotes} pts)`,
        timestamp: new Date().toISOString(),
        isMove: true,
        isRandom: wasRandomPick
      };

      const { updateData, chats } = buildMoveUpdateData({
        game, playerTeam, move,
        sourceSquare: winningMove.from,
        targetSquare: winningMove.to,
        newFen, newTurn, chatMessage,
        wasRandom: wasRandomPick,
        maxVotes,
        hasKingRole: hasKingRoleCheck(),
      });

      const batch = writeBatch(db);
      batch.update(gameRef, updateData);
      chats.forEach(c => batchPushChat(batch, gameId, c));
      await batch.commit();
      await startTimer(newTurn, 'suggestion', 90);

      const gameOverUpdate = buildGameOverUpdate(chess, playerTeam);
      if (gameOverUpdate) {
        await updateDoc(gameRef, gameOverUpdate);
      }
    } catch (error) {
      console.error('Error executing move:', error);
    }
  };

  // Handle timer expiration
  const handleTimerExpiration = async () => {
    console.log('[EXPIRATION] handleTimerExpiration called');

    if (!playerTeam || !game) return;
    if (game.status !== 'in_progress') return;
    if (game.paused) return; // host paused — no auto-execution
    if (!hasKingRoleCheck()) return;
    if (game.currentTurn !== playerTeam) return;

    const timerData = playerTeam === 'white' ? game.turnTimer?.white : game.turnTimer?.black;
    if (!timerData) return;

    const activePoll = playerTeam === 'white' ? game.activePolls?.white : game.activePolls?.black;
    const suggestions = playerTeam === 'white' ? game.suggestedMoves?.white : game.suggestedMoves?.black;

    console.log('[EXPIRATION] Executing!', {
      phase: timerData.phase,
      hasPoll: !!activePoll,
      suggestionsCount: suggestions?.length || 0
    });

    if (timerData.phase === 'voting' && activePoll) {
      await executeWinningMove(null, true);
    } else if (timerData.phase === 'suggestion') {
      if (suggestions && suggestions.length > 0) {
        const uniqueMoves = groupSuggestionsByMove(suggestions);
        const randomIndex = Math.floor(Math.random() * uniqueMoves.length);
        const randomMove = uniqueMoves[randomIndex];
        await executeRandomMove(randomMove, 'No poll created in time - random move executed');
      } else {
        await executeAnyLegalMove('No moves suggested - random legal move executed');
      }
    }
  };

  return {
    selectedMoves,
    setSelectedMoves,
    createPoll,
    voteOnMove,
    executeWinningMove,
    handleTimerExpiration,
  };
};
